import { restartableTask, timeout } from 'ember-concurrency';
import { service } from '@ember/service';
import { task } from 'reactiveweb/ember-concurrency';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Select from 'tio-ui/components/select';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { Store as StoreService } from '@warp-drive/core';
import { query, findRecord } from '@warp-drive/utilities/json-api';

const INPUT_DEBOUNCE = /* config.environment === 'test' ? 0 : */ 250;

export interface SelectEmployeeArgs {
  Args: {
    companyId: string;
    selected?: string | EmployeeModel | null;
    onChange: (value?: EmployeeModel) => void;
    label?: string;
    labelClass?: string;
    placeholder?: string;
    showPayrollId?: boolean;
    errors?: string[];
  };
  Element: HTMLDivElement;
}

export default class SelectEmployeeComponent extends Component<SelectEmployeeArgs> {
  @service declare store: StoreService;
  @tracked filterTerm = '';

  get selectedId(): string | undefined {
    const selected = this.args.selected;
    if (typeof selected === 'string') {
      return selected;
    } else if (selected && selected.id) {
      return selected.id;
    }
    return undefined;
  }

  get selectedKey() {
    return this.selectedId || '';
  }

  fetchEmployees = restartableTask(async (term: string) => {
    await timeout(INPUT_DEBOUNCE);
    const { content } = (await this.store.request(
      query<EmployeeModel>('employee', {
        'filter[search]': term,
        'filter[company]': this.args.companyId,
      })
    )) as { content: { data: EmployeeModel[] } };
    const results = content.data;

    return results
      .filter((employee) => !!employee.id)
      .map((employee) => ({
        key: employee.id!,
        label: this.args.showPayrollId
          ? `${employee.fullName} – ID # ${employee.payrollId}`
          : employee.fullName,
      }));
  });

  fetchEmployeesTask = task(this, this.fetchEmployees, () => [this.filterTerm]);

  get items() {
    let list = (this.fetchEmployeesTask.value ?? []) as { key: string; label: string }[];

    if (this.selectedId && !list.find((item) => item.key === this.selectedId)) {
      const selected = this.args.selected as EmployeeModel;
      const label =
        typeof selected === 'object'
          ? this.args.showPayrollId
            ? `${selected.fullName} – ID # ${selected.payrollId}`
            : selected.fullName
          : '';

      list = [{ key: this.selectedId, label }, ...list];
    }

    return list;
  }

  onFilterChange = (term: string) => {
    this.filterTerm = term;
  };

  handleSelectionChange = async (key: string | null) => {
    if (!key) {
      this.filterTerm = ''; // When cleared, reset the filter term
      this.args.onChange();
    } else {
      // re-fetch the full model so parent gets the actual EmployeeModel
      const { content } = (await this.store.request(
        findRecord<EmployeeModel>('employee', key)
      )) as { content: { data: EmployeeModel } };
      this.args.onChange(content.data);
    }
  };
  <template>
    <div ...attributes>
      <Select
        @label={{@label}}
        @name="employee"
        @placeholder={{@placeholder}}
        @searchable={{true}}
        @isLoading={{this.fetchEmployeesTask.isRunning}}
        @items={{this.items}}
        @selected={{this.selectedKey}}
        @onSearch={{this.onFilterChange}}
        @onChange={{this.handleSelectionChange}}
        @clearable={{true}}
        @errors={{@errors}}
        @searchDebounce={{0}}
        class="truncate"
      />
    </div>
  </template>
}
